/**
 * Settings' Phases (migration 0091; P3 of the #55 plan, §C.7.5): the second
 * tag on a line, from one list the whole business shares. A project answers
 * "for whom", a phase "which part of the work", so "all design work, every
 * client" is one filter. Optional everywhere, three levels deep, archived
 * rather than deleted once a line names it.
 *
 * Anyone signed in reads the list; whoever manages the business adds, changes
 * and archives (the API's rule, as for projects).
 */

import { useEffect, useState } from 'react';
import { ApiError, api, type Phase, type PhaseInput } from '../api.js';
import { useCan } from '../auth/AuthProvider.js';
import { parentsFor, pathOf } from '../lib/tree.js';
import { InfoNote } from './InfoNote.js';
import { Toast } from './Toast.js';

function PhaseForm(props: {
  initial: PhaseInput;
  self: string | null;
  phases: Phase[];
  submitLabel: string;
  busy: boolean;
  onSubmit: (input: PhaseInput) => void;
  onCancel: () => void;
}) {
  const [form, setForm] = useState<PhaseInput>(props.initial);
  const set = (patch: Partial<PhaseInput>) => setForm((current) => ({ ...current, ...patch }));
  // Any phase in use that leaves the tree within three levels, never this one or one under it.
  const parents = parentsFor(props.phases, props.self).filter((phase) => phase.archivedAt === null);
  return (
    <div className="new-account fund-form" role="group" aria-label={props.submitLabel}>
      <div className="field-row">
        <label className="field field-short">
          <span>Code</span>
          <input
            value={form.code ?? ''}
            maxLength={20}
            onChange={(event) => {
              const code = event.target.value;
              set({ code: code === '' ? null : code });
            }}
          />
        </label>
        <label className="field">
          <span>Name</span>
          <input
            value={form.name}
            maxLength={100}
            onChange={(event) => {
              const name = event.target.value;
              set({ name });
            }}
          />
        </label>
        <label className="field">
          <span>Under</span>
          <select
            value={form.parentId ?? ''}
            onChange={(event) => {
              const parentId = event.target.value;
              set({ parentId: parentId === '' ? null : parentId });
            }}
          >
            <option value="">Nothing (Top Level)</option>
            {parents.map((phase) => (
              <option key={phase.id} value={phase.id}>
                {pathOf(props.phases, phase.id)}
              </option>
            ))}
          </select>
        </label>
      </div>
      <button type="button" disabled={props.busy} onClick={() => props.onSubmit(form)}>
        {props.submitLabel}
      </button>
      <button type="button" className="link" onClick={props.onCancel}>
        Cancel
      </button>
    </div>
  );
}

export function PhasesSection() {
  const can = useCan();
  const manages = can('manage_business');
  const [phases, setPhases] = useState<Phase[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    api
      .listPhases()
      .then((result) => {
        if (live) setPhases(result.phases);
      })
      .catch((caught: unknown) => {
        if (live) setLoadError(caught instanceof ApiError ? caught.message : 'Could not read the phases.');
      });
    return () => {
      live = false;
    };
  }, []);

  async function act(run: () => Promise<{ phase: Phase }>, done: string) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const { phase } = await run();
      // Read again: a move changes the order and the paths of what sits under it.
      const { phases: fresh } = await api.listPhases().catch(() => ({ phases: [phase] }));
      setPhases(fresh);
      setEditing(null);
      setNotice(done);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not save the phase.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="settings-section" aria-labelledby="settings-phases">
      <h2 id="settings-phases">Phases</h2>
      <p className="muted">
        Which part of the work, across every project: a builder&rsquo;s framing and roofing, an agency&rsquo;s design.{' '}
        <InfoNote label="What A Phase Is">
          A project says whom the money was for; a phase says which part of the work it paid for, from one list every project
          shares, so one report can read all the framing, or all the design work, across every client. A builder&rsquo;s phases
          might be Site Work, Framing and Roofing; an agency&rsquo;s Strategy, Design and Media Buy; a non-profit&rsquo;s the
          lines of a grant&rsquo;s budget. A phase goes on revenue and expense lines and on an asset that holds a project&rsquo;s
          cost, never on a line holding stock, and none is required. Phases go three levels deep. One that is no longer used is
          archived, and keeps its lines.
        </InfoNote>
      </p>

      {notice && !error && <Toast message={notice} variant="ok" onDismiss={() => setNotice(null)} />}
      {loadError && (
        <p role="alert" className="error">
          {loadError}
        </p>
      )}
      {error && (
        <p role="alert" className="error">
          {error}
        </p>
      )}

      {phases === null ? (
        !loadError && <p className="muted">Loading…</p>
      ) : phases.length === 0 ? (
        <p className="muted">No phases yet.</p>
      ) : (
        <div className="table-scroll">
          <table className="lines">
            <thead>
              <tr>
                <th>Name</th>
                <th>Code</th>
                <th>Status</th>
                {manages && <th />}
              </tr>
            </thead>
            <tbody>
              {phases.map((phase) => (
                <tr key={phase.id}>
                  <td>{pathOf(phases, phase.id)}</td>
                  <td>{phase.code ?? ''}</td>
                  <td>{phase.archivedAt !== null && <span className="tag">Archived</span>}</td>
                  {manages && (
                    <td className="line-actions">
                      <button type="button" className="link" aria-label={`Change ${phase.name}`} onClick={() => setEditing(phase.id)}>
                        Change
                      </button>
                      <button
                        type="button"
                        className="link"
                        disabled={busy}
                        aria-label={`${phase.archivedAt === null ? 'Archive' : 'Restore'} ${phase.name}`}
                        onClick={() =>
                          void act(
                            () => (phase.archivedAt === null ? api.archivePhase(phase.id) : api.restorePhase(phase.id)),
                            `${phase.archivedAt === null ? 'Archived' : 'Restored'} ${phase.name}`,
                          )
                        }
                      >
                        {phase.archivedAt === null ? 'Archive' : 'Restore'}
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {manages &&
        editing !== null &&
        phases !== null &&
        (() => {
          const phase = phases.find((candidate) => candidate.id === editing);
          if (editing !== 'new' && phase === undefined) return null;
          return (
            <PhaseForm
              key={editing}
              initial={phase ? { code: phase.code, name: phase.name, parentId: phase.parentId } : { code: null, name: '', parentId: null }}
              self={phase?.id ?? null}
              phases={phases}
              submitLabel={phase ? 'Save Phase' : 'Add Phase'}
              busy={busy}
              onCancel={() => setEditing(null)}
              onSubmit={(input) => {
                // A control never does nothing: an empty name answers with a sentence.
                if (input.name.trim() === '') {
                  setError('A phase has a name.');
                  return;
                }
                void act(
                  () => (phase ? api.updatePhase(phase.id, input) : api.createPhase(input)),
                  phase ? `Saved ${input.name}` : `Added ${input.name}`,
                );
              }}
            />
          );
        })()}
      {manages && editing === null && phases !== null && (
        <button type="button" className="link" onClick={() => setEditing('new')}>
          Add a Phase
        </button>
      )}
    </section>
  );
}
